import { and, eq, inArray, sql } from 'drizzle-orm'

import type { Database } from '../client'
import type { SelectSchoolRemovalRequest } from '../schema'
import type { DatabaseTransaction } from './child-merge'
import { member, SCHOOL_REMOVAL_REQUEST_TTL_DAYS, SchoolRemovalRequest, user } from '../schema'

/**
 * A handle that may be either a pooled connection or an open transaction.
 *
 * Most of these helpers are called from inside `deleteChildren`'s transaction, where a
 * `Database`-typed parameter would still compile but run its statements on a different connection —
 * outside the transaction, and therefore not rolled back with it. Mirrors the same type in
 * `./home-connect`.
 */
type Queryable = Database | DatabaseTransaction

/** `requestedAt` plus the fixed TTL. Kept here so the request and resend paths cannot drift. */
export function schoolRemovalExpiryFrom(start: Date): Date {
  return new Date(start.getTime() + SCHOOL_REMOVAL_REQUEST_TTL_DAYS * 24 * 60 * 60 * 1000)
}

/**
 * The live request for one child at one school, if any.
 *
 * "Live" is PENDING *and* inside its window, which is stricter than the partial unique index —
 * see {@link expireLapsedRemovalRequest} for why that gap matters.
 */
export async function findLiveRemovalRequest(
  db: Queryable,
  childUserId: string,
  schoolOrganizationId: string,
  now = new Date(),
): Promise<SelectSchoolRemovalRequest | null> {
  const [row] = await db
    .select()
    .from(SchoolRemovalRequest)
    .where(
      and(
        eq(SchoolRemovalRequest.childUserId, childUserId),
        eq(SchoolRemovalRequest.schoolOrganizationId, schoolOrganizationId),
        eq(SchoolRemovalRequest.status, 'PENDING'),
        sql`${SchoolRemovalRequest.expiresAt} > ${now}`,
      ),
    )
    .limit(1)

  return row ?? null
}

/**
 * One request, but only if this parent is the one who opened it.
 *
 * Matched on `requestedBy` rather than re-deriving the family from the child: the resend path must
 * not let a second adult in the same family reopen a window someone else's budget paid for.
 */
export async function findRemovalRequestForParent(
  db: Queryable,
  requestId: string,
  requestedBy: string,
): Promise<SelectSchoolRemovalRequest | null> {
  const [row] = await db
    .select()
    .from(SchoolRemovalRequest)
    .where(and(eq(SchoolRemovalRequest.id, requestId), eq(SchoolRemovalRequest.requestedBy, requestedBy)))
    .limit(1)

  return row ?? null
}

/**
 * Settle a lapsed request before anything tries to insert a new one.
 *
 * The read guards treat a PENDING row past its window as absent, but the partial unique index is
 * predicated on `status = 'PENDING'` alone and knows nothing about `expiresAt`. Between a request
 * lapsing and the nightly sweep reaching it those two views disagree, and the insert fails with a raw
 * unique violation — a 500 rather than a message. Calling this first removes the disagreement instead
 * of teaching the index about time.
 */
export async function expireLapsedRemovalRequest(
  db: Queryable,
  childUserId: string,
  schoolOrganizationId: string,
  now = new Date(),
): Promise<number> {
  const rows = await db
    .update(SchoolRemovalRequest)
    .set({ status: 'EXPIRED' })
    .where(
      and(
        eq(SchoolRemovalRequest.childUserId, childUserId),
        eq(SchoolRemovalRequest.schoolOrganizationId, schoolOrganizationId),
        eq(SchoolRemovalRequest.status, 'PENDING'),
        sql`${SchoolRemovalRequest.expiresAt} <= ${now}`,
      ),
    )
    .returning()

  return rows.length
}

/**
 * Mark these children's live requests against this school as answered.
 *
 * Called from `deleteChildren` when the school actually removes the student — the one thing the
 * parent asked for. Scoped to the school doing the removing so a request naming a different school
 * survives.
 */
export async function resolveRemovalRequestsForChildren(
  db: Queryable,
  childUserIds: string[],
  schoolOrganizationId: string,
): Promise<number> {
  if (childUserIds.length === 0) return 0

  const rows = await db
    .update(SchoolRemovalRequest)
    .set({ status: 'RESOLVED', resolvedAt: new Date() })
    .where(
      and(
        inArray(SchoolRemovalRequest.childUserId, childUserIds),
        eq(SchoolRemovalRequest.schoolOrganizationId, schoolOrganizationId),
        eq(SchoolRemovalRequest.status, 'PENDING'),
      ),
    )
    .returning()

  return rows.length
}

/**
 * Cancel every live request for these children, whatever school it names.
 *
 * For a child whose account is going away entirely. The rows would cascade with the `user` row
 * anyway; the explicit call covers the case where the request names a different organization than
 * the one performing the deletion, and keeps the audit trail readable either way.
 */
export async function cancelRemovalRequestsForChildren(db: Queryable, childUserIds: string[]): Promise<number> {
  if (childUserIds.length === 0) return 0

  const rows = await db
    .update(SchoolRemovalRequest)
    .set({ status: 'CANCELLED', cancelledAt: new Date() })
    .where(and(inArray(SchoolRemovalRequest.childUserId, childUserIds), eq(SchoolRemovalRequest.status, 'PENDING')))
    .returning()

  return rows.length
}

/**
 * Flip requests whose window has closed. Reads already treat an out-of-window PENDING row as
 * expired, so this only keeps the stored state honest for reporting and frees the unique slot.
 */
export async function expireStaleRemovalRequests(db: Database, now = new Date()): Promise<number> {
  const rows = await db
    .update(SchoolRemovalRequest)
    .set({ status: 'EXPIRED' })
    .where(and(eq(SchoolRemovalRequest.status, 'PENDING'), sql`${SchoolRemovalRequest.expiresAt} <= ${now}`))
    .returning()

  return rows.length
}

export interface SchoolAdminRecipient {
  userId: string
  name: string
  email: string
}

/**
 * Every owner and admin of a school, with the addresses to mail.
 *
 * The audience for a parent's removal request: a guru kelas cannot remove a student from the school,
 * so telling them would be a notification they can do nothing about. Returns names and emails
 * alongside the ids because the same list feeds both `emit` and the mail task.
 */
export async function findSchoolAdminRecipients(
  db: Queryable,
  schoolOrganizationId: string,
): Promise<SchoolAdminRecipient[]> {
  const rows = await db
    .select({ userId: member.userId, name: user.name, email: user.email })
    .from(member)
    .innerJoin(user, eq(user.id, member.userId))
    .where(and(eq(member.organizationId, schoolOrganizationId), inArray(member.role, ['owner', 'admin'])))

  return rows
}
